import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { useCSRF } from './hooks/useCSRF';
import { ThemeProvider } from './contexts/ThemeContext';
import LandingPage from './components/LandingPage';
import Signup from './components/Signup';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Verify from './components/Verify';
import Dashboard from './components/Dashboard';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import RefundPolicy from './components/RefundPolicy';
import Profile from './components/Profile';
import OrganizationManagement from './components/OrganizationManagement';
import WelcomeInvitation from './components/WelcomeInvitation';
import Upgrade from './components/Upgrade';
import Admin from './components/Admin';
import PaymentConfirmation from './components/PaymentConfirmation';
import InvoiceCancel from './components/InvoiceCancel';
import AuditLogs from './components/AuditLogs';

function PrivateRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = React.useState(null); // null = checking, true/false = result
  
  React.useEffect(() => {
    // Check if user has valid session via cookie
    fetch(`${process.env.REACT_APP_API_BASE_URL || window.location.origin}/api/profile`, {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => {
        setIsAuthenticated(res.ok);
      })
      .catch(() => {
        setIsAuthenticated(false);
      });
  }, []);
  
  if (isAuthenticated === null) {
    // Still checking authentication
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function App() {
  // Fetch CSRF token on app load
  useCSRF();
  
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify/:token" element={<Verify />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/organization" element={<PrivateRoute><OrganizationManagement /></PrivateRoute>} />
          <Route path="/upgrade" element={<PrivateRoute><Upgrade /></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute><Admin /></PrivateRoute>} />
          <Route path="/audit-logs" element={<PrivateRoute><AuditLogs /></PrivateRoute>} />
          <Route path="/invoice" element={<Navigate to="/dashboard" replace />} />
          <Route path="/invoice/:id/payment" element={<PrivateRoute><PaymentConfirmation /></PrivateRoute>} />
          <Route path="/invoice/:id/cancel" element={<PrivateRoute><InvoiceCancel /></PrivateRoute>} />
          <Route path="/welcome/:token/*" element={<WelcomeInvitation />} />
          <Route path="/accept-invitation/:token/*" element={<WelcomeInvitation />} />
        </Routes>
      </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
